import {
    CollectionDataLoaded,
    ContentDataTypes,
    DataLoaderGlobalParams,
    loadCollectionData,
} from '@news-mono/web-common'
import React, { useEffect, useState } from 'react'
import { Button } from '../../buttons/Button/Button'
import { StyledButtonContainer } from '../../collections-new-loadmore/LoadMore/LoadMore.styled'

interface LoadedData {
    loaded: true
    result: CollectionDataLoaded
}
type Data = { loaded: false } | LoadedData

export interface LoadMoreRouteProps {
    loadMoreEnabled?: boolean
}

interface LoadMoreProps {
    dataDefinition: ContentDataTypes
    data: Data
    services: DataLoaderGlobalParams
    loadMoreEnabled: boolean
    render: (data: Data) => JSX.Element | JSX.Element[]
    onLoadMore: (data: LoadedData) => void
}

export const LoadMore: React.FC<LoadMoreProps> = (props) => {
    const currentDataDefinition = getDataDefinition(props)

    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState(props.data)
    const [dataDefinition, setDataDefinition] = useState(currentDataDefinition)

    // When the data or data definition from the props change, reset the current state
    useEffect(() => {
        setIsLoading(false)
        setData(props.data)
        setDataDefinition(currentDataDefinition)
    }, [props.data, currentDataDefinition])

    async function nextPage() {
        if (
            dataDefinition &&
            dataDefinition.type === 'listing' &&
            dataDefinition.paging
        ) {
            const newDataDefinition = {
                ...dataDefinition,
                paging: {
                    ...dataDefinition.paging,
                    page: dataDefinition.paging.page + 1,
                },
            }
            setDataDefinition(newDataDefinition)
            return await loadCollectionData(props.services, newDataDefinition)
        }
        return undefined
    }

    async function loadMore(loadedData: LoadedData) {
        setIsLoading(true)
        const result = await nextPage()
        if (
            result &&
            result.kind === 'listing' &&
            loadedData.result.kind === 'listing'
        ) {
            // Lets update the data with some more data, adhereing to the loadedData type format
            const updatedData: LoadedData = {
                loaded: true,
                result: {
                    publications: loadedData.result.publications.concat(
                        result.publications,
                    ),
                    kind: 'listing',
                    loadMorePossible: result.loadMorePossible,
                },
            }
            props.onLoadMore({
                loaded: true,
                result: {
                    publications: result.publications,
                    kind: 'listing',
                    loadMorePossible: result.loadMorePossible,
                },
            })
            // Update the state
            setIsLoading(false)
            setData(updatedData)
        } else {
            throw new Error('Error loading next page')
        }
    }

    function loadMoreButton() {
        if (
            props.loadMoreEnabled &&
            data.loaded &&
            data.result.loadMorePossible &&
            dataDefinition &&
            dataDefinition.type === 'listing' &&
            dataDefinition.paging
        ) {
            return (
                <StyledButtonContainer>
                    <Button
                        className={'gtm-load-more-button'}
                        disabled={isLoading}
                        onClick={() => loadMore(data)}
                    >
                        {isLoading ? 'Loading...' : 'Load More'}
                    </Button>
                </StyledButtonContainer>
            )
        }
        return null
    }

    return (
        <React.Fragment>
            {props.render(data)}
            {loadMoreButton()}
        </React.Fragment>
    )
}

function getDataDefinition(props: LoadMoreProps) {
    return props.dataDefinition.type !== 'aggregated'
        ? props.dataDefinition
        : undefined
}
